import {
  emptyBpCargoItemRow,
  type BpCargoItemRow,
  bpCargoRowsToEtrnCargo,
  bpCargoRowsToEzzCargo,
} from "./bp-cargo-items";

/** Код хранилища данных приложения «Грузовые места ЭПД». */
export const EPD_CARGO_ENTITY_CODE = "epd_cargo";
export const EPD_CARGO_ENTITY_NAME = "Грузовые места ЭПД";

export const EPD_CARGO_PROPERTY_DEFS = [
  { property: "owner_key", name: "Ключ владельца" },
  { property: "owner_type", name: "Тип владельца" },
  { property: "owner_id", name: "ID владельца" },
  { property: "name", name: "Наименование груза" },
  { property: "places_count", name: "Число грузовых мест" },
  { property: "package_kind", name: "Вид тары" },
  { property: "packaging", name: "Способ упаковки" },
  { property: "condition", name: "Состояние" },
  { property: "volume", name: "Объём" },
  { property: "height", name: "Высота" },
  { property: "length", name: "Длина" },
  { property: "width", name: "Ширина" },
  { property: "gross_mass", name: "Масса брутто" },
  { property: "net_mass", name: "Масса нетто" },
  { property: "mass_method", name: "Метод массы" },
  { property: "can_distribute", name: "Распределение" },
  { property: "is_divisible", name: "Делимость" },
] as const;

export type EpdCargoPropertyCode =
  (typeof EPD_CARGO_PROPERTY_DEFS)[number]["property"];

const ROW_TO_PROP: Record<keyof BpCargoItemRow, EpdCargoPropertyCode | null> = {
  name: "name",
  placesCount: "places_count",
  packageKind: "package_kind",
  packaging: "packaging",
  condition: "condition",
  volume: "volume",
  height: "height",
  length: "length",
  width: "width",
  grossMass: "gross_mass",
  netMass: "net_mass",
  massMethod: "mass_method",
  canDistribute: "can_distribute",
  isDivisible: "is_divisible",
  palletsCount: null,
  tareMass: null,
  placesMass: null,
  containerInfo: null,
  driverParticipation: null,
  otherInfo: null,
};

export interface EpdCargoOwnerRef {
  ownerKey: string;
  ownerType: string;
  ownerId: string;
}

export interface EpdCargoEntityItem {
  id: number;
  name: string;
  owner: EpdCargoOwnerRef;
  row: BpCargoItemRow;
}

function propValue(
  propertyValues: Record<string, unknown> | undefined,
  code: string
): string {
  if (!propertyValues) {
    return "";
  }
  const direct = propertyValues[code];
  if (direct !== undefined && direct !== null) {
    return String(Array.isArray(direct) ? direct[0] : direct).trim();
  }
  const upper = propertyValues[code.toUpperCase()];
  if (upper !== undefined && upper !== null) {
    return String(Array.isArray(upper) ? upper[0] : upper).trim();
  }
  return "";
}

export function parseEpdCargoOwnerKey(ownerKey: string): EpdCargoOwnerRef | null {
  const key = String(ownerKey || "").trim().toUpperCase();
  if (!key) {
    return null;
  }
  const deal = key.match(/^DEAL_(\d+)$/);
  if (deal) {
    return { ownerKey: key, ownerType: "DEAL", ownerId: deal[1]! };
  }
  const dynamic = key.match(/^DYNAMIC_(\d+)_(\d+)$/);
  if (dynamic) {
    return {
      ownerKey: key,
      ownerType: `DYNAMIC_${dynamic[1]}`,
      ownerId: dynamic[2]!,
    };
  }
  return null;
}

export function ownerRefFromBizprocDocument(params: {
  documentType: [string, string, string];
  documentId: [string, string, string];
}): EpdCargoOwnerRef {
  const typeCode = String(params.documentType[2] || "").trim().toUpperCase();
  const docId = String(params.documentId[2] || "").trim();

  if (typeCode === "DEAL" || docId.toUpperCase().startsWith("DEAL_")) {
    const id = docId.replace(/^DEAL_/i, "").replace(/\D/g, "");
    if (!id) {
      throw new Error("Не удалось определить ID сделки из document_id");
    }
    return { ownerKey: `DEAL_${id}`, ownerType: "DEAL", ownerId: id };
  }

  if (typeCode.startsWith("DYNAMIC_") || docId.toUpperCase().startsWith("DYNAMIC_")) {
    const full = docId.match(/^DYNAMIC_(\d+)_(\d+)$/i);
    if (full) {
      return {
        ownerKey: `DYNAMIC_${full[1]}_${full[2]}`.toUpperCase(),
        ownerType: `DYNAMIC_${full[1]}`,
        ownerId: full[2]!,
      };
    }
    const typeOnly = typeCode.match(/^DYNAMIC_(\d+)$/i);
    const idTail = docId.match(/_(\d+)$/);
    if (typeOnly && idTail) {
      return {
        ownerKey: `DYNAMIC_${typeOnly[1]}_${idTail[1]}`.toUpperCase(),
        ownerType: `DYNAMIC_${typeOnly[1]}`,
        ownerId: idTail[1]!,
      };
    }
  }

  throw new Error(
    `Грузовые места ЭПД не поддерживают тип «${typeCode || docId}». Ожидается сделка или смарт-процесс.`
  );
}

export function ownerRefFromPlacement(params: {
  placement: string;
  entityId: string | number;
}): EpdCargoOwnerRef {
  const placement = String(params.placement || "").trim().toUpperCase();
  const entityId = String(params.entityId || "").replace(/\D/g, "");
  if (!entityId) {
    throw new Error("Не удалось определить ID элемента CRM");
  }
  if (placement === "CRM_DEAL_DETAIL_TAB") {
    return {
      ownerKey: `DEAL_${entityId}`,
      ownerType: "DEAL",
      ownerId: entityId,
    };
  }
  const dynamic = placement.match(/^CRM_DYNAMIC_(\d+)_DETAIL_TAB$/);
  if (dynamic) {
    return {
      ownerKey: `DYNAMIC_${dynamic[1]}_${entityId}`,
      ownerType: `DYNAMIC_${dynamic[1]}`,
      ownerId: entityId,
    };
  }
  throw new Error(`Неизвестный placement вкладки груза: ${placement}`);
}

export function bpCargoRowToPropertyValues(
  row: BpCargoItemRow,
  owner: EpdCargoOwnerRef
): Record<string, string> {
  const values: Record<string, string> = {
    owner_key: owner.ownerKey,
    owner_type: owner.ownerType,
    owner_id: owner.ownerId,
  };
  for (const [rowKey, prop] of Object.entries(ROW_TO_PROP)) {
    if (!prop) {
      continue;
    }
    values[prop] = String(row[rowKey as keyof BpCargoItemRow] ?? "").trim();
  }
  return values;
}

export function propertyValuesToBpCargoRow(
  propertyValues: Record<string, unknown> | undefined
): BpCargoItemRow {
  const empty = emptyBpCargoItemRow();
  return {
    ...empty,
    name: propValue(propertyValues, "name") || empty.name,
    placesCount: propValue(propertyValues, "places_count") || empty.placesCount,
    packageKind: propValue(propertyValues, "package_kind") || empty.packageKind,
    packaging: propValue(propertyValues, "packaging") || empty.packaging,
    condition: propValue(propertyValues, "condition") || empty.condition,
    volume: propValue(propertyValues, "volume") || empty.volume,
    height: propValue(propertyValues, "height") || empty.height,
    length: propValue(propertyValues, "length") || empty.length,
    width: propValue(propertyValues, "width") || empty.width,
    grossMass: propValue(propertyValues, "gross_mass") || empty.grossMass,
    netMass: propValue(propertyValues, "net_mass") || empty.netMass,
    massMethod: propValue(propertyValues, "mass_method") || empty.massMethod,
    canDistribute:
      propValue(propertyValues, "can_distribute") || empty.canDistribute,
    isDivisible: propValue(propertyValues, "is_divisible") || empty.isDivisible,
  };
}

export function parseEntityItemToCargo(
  raw: Record<string, unknown>
): EpdCargoEntityItem | null {
  const id = Number(raw.ID ?? raw.id);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  const propertyValues = (raw.PROPERTY_VALUES ||
    raw.propertyValues ||
    {}) as Record<string, unknown>;
  const row = propertyValuesToBpCargoRow(propertyValues);
  const ownerKey = propValue(propertyValues, "owner_key");
  const owner =
    parseEpdCargoOwnerKey(ownerKey) ||
    ({
      ownerKey,
      ownerType: propValue(propertyValues, "owner_type"),
      ownerId: propValue(propertyValues, "owner_id"),
    } satisfies EpdCargoOwnerRef);

  return {
    id,
    name: String(raw.NAME || raw.name || row.name || "").trim() || row.name,
    owner,
    row,
  };
}

export { bpCargoRowsToEtrnCargo, bpCargoRowsToEzzCargo };
